import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ChevronLeft, Loader2, Pause, SkipForward } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { VoiceInterviewStub } from "@/components/onboarding/VoiceInterviewStub";
import { InterviewAttachments } from "@/components/interview/InterviewAttachments";
import { InterviewComplete } from "@/components/interview/InterviewComplete";
import { InterviewProgress } from "@/components/interview/InterviewProgress";
import { QuestionPlayer } from "@/components/interview/QuestionPlayer";
import { trackSequentialAnswerSkipped } from "@/lib/analytics-events";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/use-session";
import { useSequentialInterview } from "@/hooks/useSequentialInterview";

export const Route = createFileRoute("/_app/_/interview/session")({
	component: SequentialInterviewPage,
});

function SequentialInterviewPage() {
	const session = useSession();
	const campaignSlug = session?.campaign?.slug;
	const [retryCount, setRetryCount] = useState(0);

	useEffect(() => {
		if (!campaignSlug && retryCount < 3) {
			const timer = setTimeout(() => {
				setRetryCount((c) => c + 1);
			}, 1000)
			return () => clearTimeout(timer);
		}
	}, [campaignSlug, retryCount]);

	if (!campaignSlug && retryCount < 3) {
		return (
			<div className="h-full bg-background flex flex-col items-center justify-center px-6">
				<div className="text-center space-y-4">
					<Loader2 className="h-12 w-12 text-primary mx-auto animate-spin" />
					<h2 className="text-xl font-semibold text-foreground">
						Loading Your Interview
					</h2>
					<p className="text-muted-foreground">
						Preparing your campaign session...
					</p>
				</div>
			</div>
		)
	}

	if (!campaignSlug) {
		return (
			<div className="h-full bg-background flex flex-col items-center justify-center px-6">
				<div className="text-center space-y-4 max-w-md">
					<AlertCircle className="h-12 w-12 text-destructive mx-auto" />
					<h2 className="text-xl font-semibold text-foreground">
						Session Not Found
					</h2>
					<p className="text-muted-foreground">
						Unable to load your campaign information. Please try logging in
						again.
					</p>
					<Button
						onClick={() => {
							window.location.href = "/login";
						}}
						className="mt-4"
					>
						Log in again
					</Button>
				</div>
			</div>
		)
	}

	const firstName = session?.user?.first_name || "";
	return (
		<SequentialInterviewSession
			campaignSlug={campaignSlug}
			firstName={firstName}
		/>
	)
}

function SequentialInterviewSession({
	campaignSlug,
	firstName,
}: {
	campaignSlug: string;
	firstName: string;
}) {
	const {
		state,
		currentQuestionIndex,
		totalQuestions,
		currentQuestion,
		savedAnswers,
		isCompleted,
		isRecording,
		transcript,
		interimTranscript,
		isSpeechSupported,
		sttError,
		error,
		startInterview,
		submitAnswer,
		skipQuestion,
		goBack,
		goToQuestion: _goToQuestion,
		completeInterview: _completeInterview,
		pauseInterview,
		startRecording,
		stopRecording,
	} = useSequentialInterview(campaignSlug, firstName);

	// Track whether we're showing the attachments screen (between completion and thank-you)
	const [showAttachments, setShowAttachments] = useState(true);

	// Auto-start once when idle — skip the "Ready to Begin" screen.
	// Use a ref to prevent infinite retry loops if startInterview() fails
	// (it sets state back to "idle" on error, which would re-trigger this effect).
	const hasAutoStartedRef = useRef(false);
	useEffect(() => {
		if (state === "idle" && !hasAutoStartedRef.current) {
			hasAutoStartedRef.current = true;
			startInterview();
		}
	}, [state, startInterview]);

	// Error state — auto-start failed, show retry button
	if (state === "idle" && error) {
		return (
			<div className="h-full bg-background flex flex-col items-center justify-center px-6">
				<div className="text-center space-y-4 max-w-md">
					<AlertCircle className="h-12 w-12 text-destructive mx-auto" />
					<h2 className="text-xl font-semibold text-foreground">
						Unable to Start Interview
					</h2>
					<p className="text-muted-foreground">{error}</p>
					<Button
						onClick={() => {
							hasAutoStartedRef.current = false;
							startInterview();
							hasAutoStartedRef.current = true;
						}}
						className="mt-4"
					>
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	// Complete state — show attachments, then completion screen
	if (state === "complete" || isCompleted) {
		if (showAttachments) {
			return (
				<InterviewAttachments
					campaignSlug={campaignSlug}
					onContinue={() => setShowAttachments(false)}
				/>
			);
		}
		return <InterviewComplete />;
	}

	// Loading state
	if (state === "loading") {
		return (
			<div className="h-full bg-background flex flex-col items-center justify-center px-6">
				<div className="text-center space-y-4">
					<Loader2 className="h-12 w-12 text-primary mx-auto animate-spin" />
					<h2 className="text-xl font-semibold text-foreground">
						Preparing Your Interview
					</h2>
					<p className="text-muted-foreground">Loading questions...</p>
				</div>
			</div>
		)
	}

	// Active interview states
	const showAnswerSection =
		state === "waiting_for_answer" ||
		state === "recording" ||
		state === "submitting";

	return (
		<div className="min-h-screen bg-background flex flex-col">
			{/* Header: Pause button only */}
			<div className="relative bg-border/20 border-b border-border">
				<div className="flex items-center justify-end py-6 px-4">
					<PauseInterviewDialog onPause={pauseInterview} />
				</div>
			</div>

			{/* Progress bar (full-width, no padding) */}
			<InterviewProgress
				currentQuestionIndex={currentQuestionIndex}
				totalQuestions={totalQuestions}
				answeredCount={savedAnswers.size}
			/>

			{/* Main content area */}
			<div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
				<div className="w-full max-w-2xl flex flex-col gap-8">
					{/* Error banner */}
					{error && (
						<div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
							<p className="text-sm text-destructive">{error}</p>
						</div>
					)}

					{/* Question player (avatar + question card) */}
					{currentQuestion && (
						<QuestionPlayer
							questionText={currentQuestion.question_text}
							questionKey={currentQuestionIndex}
						/>
					)}

					{/* Answer recorder — fades in */}
					<div
						className="transition-opacity duration-150"
						style={{
							opacity: showAnswerSection ? 1 : 0,
							pointerEvents: showAnswerSection ? "auto" : "none",
						}}
					>
						<div className="space-y-8">
							<VoiceInterviewStub
								isRecording={isRecording}
								transcript={transcript}
								interimTranscript={interimTranscript}
								isSpeechSupported={isSpeechSupported}
								isSubmitting={state === "submitting"}
								questionIndex={currentQuestionIndex}
								existingAnswer={savedAnswers.get(currentQuestionIndex)}
								sttError={sttError}
								forceTextMode={false}
								isActive={showAnswerSection}
								onStartRecording={startRecording}
								onStopRecording={stopRecording}
								onSubmit={submitAnswer}
							/>

							{/* Back / Skip navigation */}
							<div className="flex items-center justify-between">
								<Button
									variant="ghost"
									size="sm"
									onClick={goBack}
									disabled={currentQuestionIndex === 0 || state === "submitting"}
									className="gap-1.5 text-muted-foreground"
								>
									<ChevronLeft className="h-4 w-4" />
									Back
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										trackSequentialAnswerSkipped(currentQuestionIndex);
										skipQuestion();
									}}
									disabled={state === "submitting"}
									className="gap-1.5 text-muted-foreground"
								>
									Skip
									<SkipForward className="h-3.5 w-3.5" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

/**
 * Pause interview AlertDialog.
 * Confirms whether user wants to exit or continue.
 */
function PauseInterviewDialog({ onPause }: { onPause: () => void }) {
	const navigate = useNavigate();

	const handleLogout = async () => {
		try {
			const { apiService } = await import("@/lib/api-service");
			await apiService.logout();
		} catch {
			// Continue with local logout even if server call fails
		}
		const { authService } = await import("@/lib/auth-service");
		authService.clearTokens();
		navigate({ to: "/login" });
	};

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<Button variant="outline" size="sm" className="gap-1.5 text-sm">
					<Pause className="h-3.5 w-3.5" />
					Pause Interview
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Pause Interview?</AlertDialogTitle>
					<AlertDialogDescription>
						Your progress is saved automatically. You can return anytime to pick
						up where you left off.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter className="flex-col sm:flex-row gap-2">
					<AlertDialogCancel>Resume Interview</AlertDialogCancel>
					<AlertDialogAction onClick={onPause}>
						Go to Dashboard
					</AlertDialogAction>
					<AlertDialogAction
						onClick={handleLogout}
						className="bg-muted text-muted-foreground hover:bg-muted/80"
					>
						Log Out
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export default SequentialInterviewPage;
